//#region Imports

import { Component, effect, OnDestroy, OnInit, Signal } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MenuService } from '../menu/services/menu.service';
import { ParamsService } from '../../services/params/params.service';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '../../services/theme/theme.service';
import { ThemeEnum } from '../../services/theme/models/enums/theme.enum';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterLink } from '@angular/router';
import { MeService } from '../../services/me/me.service';
import { AuthService } from '../../services/auth/auth.service';
import { OutsideClick } from '../../directives/outside-click/outside-click.directive';
import { filter, map, Subscription, switchMap } from "rxjs";
import { HeaderItem, ProfileAction } from './models/header';
import { User } from '../../models/interfaces/user.interface';
import { AvatarComponent } from '../avatar/avatar.component';

//#endregion

@Component({
    selector: 'l-header',
    standalone: true,
    templateUrl: './header.component.html',
    imports: [
    IconComponent,
    AvatarComponent,
    NgTemplateOutlet,
    FormsModule,
    RouterLink,
    OutsideClick,
    NgClass
    ]
})
export class HeaderComponent implements OnInit, OnDestroy {

  //#region Constructor

  constructor(
    router: Router,
    route: ActivatedRoute,
    private readonly meService: MeService,
    private readonly menuService: MenuService,
    private readonly authService: AuthService,
    private readonly themeService: ThemeService,
    private readonly paramsService: ParamsService,
  ) {
    effect(() => {
      const params = this.paramsService.params();
      this.inputValue = params.search ?? '';
    })

    this.routerSubscription = router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => route),
      map(route => {
        while (route.firstChild)
          route = route.firstChild;

        return route;
      }),
      switchMap(route => route.data)
      ).subscribe((data: Data) => {
        this.search = data['search'] ?? true;
    });
  }

  //#endregion

  //#region Public Properties

  public timer: any;

  public inputValue: string = '';

  public open: boolean = false;

  public search: boolean = true;

  public shortMode: Signal<boolean> = this.menuService.short;

  public theme: Signal<ThemeEnum> = this.themeService.theme;

  public me: User | null = null;

  public get avatarUrl(): string {
    return this.me?.imageUrl ?? '';
  }

  public get menuItems(): HeaderItem[] {
    return [
      { label: 'Home', link: '/home' },
      { label: 'Meus cursos', link: '/courses' },
      { label: 'Trilhas', link: '/tracks' },
    ];
  }

  public profileActions: ProfileAction[] = [
    {
      label: 'Perfil',
      icon: 'person',
      type: 'link',
      link: 'profile',
    },
    {
      label: 'Sair',
      icon: 'logout',
      type: 'action',
      action: () => void this.logout(),
    }
  ];

  //#endregion

  //#region Protected Properties

  protected readonly themeEnum = ThemeEnum;

  protected readonly routerSubscription: Subscription;

  //#endregion

  //#region Getters and Setters
  
  public get activatedIndex(): number {
    const current = location.pathname;
    return this.menuItems.findIndex(item => current.startsWith(item.link));
  }

  //#endregion

  //#region Public Methods

  public ngOnInit(): void {
    void this.loadMe();
  }

  public ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  public toggleTheme(): void {
    this.themeService.theme = this.theme() === ThemeEnum.DARK ? ThemeEnum.LIGHT : ThemeEnum.DARK;
  }

  public setShortMode(): void {
    this.menuService.short = false;
  }

  public async setSearch(): Promise<void> {
    this.paramsService.params = {
      search: this.inputValue,
    };
  }

  public async logout(): Promise<void> {
    await this.authService.logout();
  }

  public toggleMenu(): void {
    this.open = !this.open;
  }

  //#endregion

  //#region Private Methods

  private async loadMe(): Promise<void> {
    this.me = this.meService.getMe() ?? await this.meService.loadMe();
  }

  //#endregion

}
